import { useEffect, useMemo, useState } from 'react'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { Pave, appliquerTouche } from './Pave'

/**
 * La porte du Bilan.
 *
 * Le chiffre d'affaires, les marges et les sommes dues ne regardent pas
 * l'équipe de comptoir. Le code est demandé **à chaque ouverture de l'écran**,
 * y compris à un gérant déjà connecté : l'iPad reste allumé sur le comptoir
 * toute la journée, une session ouverte ne prouve pas qui est devant.
 *
 * Le laissez-passer ne vit que dans cet état React : quitter l'écran le perd,
 * et l'on redemande le code au retour. C'est voulu, pas un oubli.
 */
function PasseGerant({
  jeton,
  children,
}: {
  jeton: string
  children: (entetes: Record<string, string>) => React.ReactNode
}) {
  const entetes = useMemo(() => ({ 'x-gerant': jeton }), [jeton])
  return <>{children(entetes)}</>
}

export function PorteGerant({
  children,
  titre,
  compact = false,
}: {
  children: (entetes: Record<string, string>) => React.ReactNode
  /** Ce qu'on protège, dit à celui qui doit taper le code. */
  titre?: string
  /**
   * Porte posée à l'intérieur d'un écran déjà ouvert — une fenêtre de clôture,
   * un bloc de fiche — plutôt qu'en travers d'un onglet entier. Le pavé s'y
   * affiche seul, sans le bandeau de titre qui ferait doublon.
   */
  compact?: boolean
}) {
  const [jeton, setJeton] = useState<string | null>(null)
  const [defini, setDefini] = useState<boolean | null>(null)
  const [code, setCode] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    api<{ defini: boolean }>('/code-gerant')
      .then((r) => setDefini(r.defini))
      .catch(() => setDefini(true))
  }, [])

  const premierCode = defini === false

  const valider = async () => {
    if (envoi) return
    setErreur(null)
    if (premierCode) {
      if (code.length < 4) return setErreur('Choisissez un code de 4 à 8 chiffres.')
      if (code !== confirmation) return setErreur('Les deux saisies diffèrent.')
    } else if (code.length < 4) {
      return setErreur('Code incomplet.')
    }
    setEnvoi(true)
    try {
      const reponse = premierCode
        ? await api<{ jeton: string }>('/code-gerant', { methode: 'POST', corps: { code } })
        : await api<{ jeton: string }>('/code-gerant/verifier', {
            methode: 'POST',
            corps: { code },
          })
      setJeton(reponse.jeton)
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Vérification impossible.')
      setCode('')
      setConfirmation('')
    } finally {
      setEnvoi(false)
    }
  }

  const cible = premierCode && code.length >= 4 ? confirmation : code
  const majCible = (valeur: string) =>
    premierCode && code.length >= 4 ? setConfirmation(valeur) : setCode(valeur)

  // Clavier physique : mêmes chiffres qu'au pavé tactile, Backspace pour
  // effacer un chiffre, Échap pour tout vider.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) {
        setErreur(null)
        majCible(appliquerTouche(cible, e.key).replace(/\D/g, '').slice(0, 8))
      } else if (e.key === 'Backspace') {
        setErreur(null)
        majCible(cible.slice(0, -1))
      } else if (e.key === 'Escape') {
        setErreur(null)
        majCible('')
      }
    }
    window.addEventListener('keydown', surTouche)
    return () => window.removeEventListener('keydown', surTouche)
  })

  // Validation automatique une fois le code plausible et la frappe arrêtée :
  // la longueur du code gérant varie de 4 à 8 chiffres, donc pas de
  // déclenchement figé comme pour le PIN vendeur — on attend une courte pause
  // sans nouvelle touche plutôt que de tenter à chaque quatrième chiffre.
  useEffect(() => {
    if (cible.length < 4 || envoi) return
    const minuterie = window.setTimeout(() => void valider(), 400)
    return () => window.clearTimeout(minuterie)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cible, envoi])

  // Ces deux sorties viennent après tous les Hooks ci-dessus : un `return`
  // placé plus tôt changerait leur nombre d'un rendu à l'autre (code entré
  // → jeton posé, ou chargement → réponse connue) et ferait planter React.
  if (jeton) return <PasseGerant jeton={jeton}>{children}</PasseGerant>

  if (defini === null) {
    const attente = <div className="vide">Un instant…</div>
    return compact ? attente : (
      <Ecran titre={titre ?? 'Bilan'} sousTitre="Vérification…">{attente}</Ecran>
    )
  }

  const saisie = (
    // Le centrage vertical sur toute la hauteur (`.connexion`) suppose un
    // écran plein — dans une fenêtre déjà ouverte (clôture, fiche), la zone
    // défilante est plus haute que l'écran dès qu'il y a du texte au-dessus,
    // et centrer dedans pousse le pavé bien sous le pli. En mode compact, la
    // boîte reste simplement dans le flux, sans grille de centrage.
    <div className={compact ? 'connexion-compacte' : 'connexion'}>
        <div className="connexion-boite">
          <div className="etiquette">
            {premierCode
              ? code.length >= 4
                ? 'Confirmez le code'
                : 'Choisissez le code gérant'
              : 'Code gérant'}
          </div>

          <div className="pin-points" aria-label={`${cible.length} chiffres saisis`}>
            {Array.from({ length: Math.max(4, cible.length) }, (_, i) => (
              <span key={i} className={i < cible.length ? 'pin-point plein' : 'pin-point'} />
            ))}
          </div>

          {erreur && (
            <div className="note" style={{ borderColor: 'var(--color-accent-800)' }}>
              {erreur}
            </div>
          )}

          <Pave
            decimales={false}
            onTouche={(touche) => {
              setErreur(null)
              majCible(appliquerTouche(cible, touche).replace(/\D/g, '').slice(0, 8))
            }}
            onEffacer={() => {
              setErreur(null)
              majCible('')
            }}
          />

          <button
            type="button"
            className="btn btn-primaire btn-bloc btn-large"
            disabled={envoi || cible.length < 4}
            onClick={() => void valider()}
          >
            {premierCode ? 'Enregistrer le code' : `Ouvrir ${titre ? '' : 'le bilan'}`.trim() || 'Ouvrir'}
          </button>

          <div className="note">
            {premierCode
              ? 'Ce code protège le chiffre d’affaires et les reversements. De 4 à 8 chiffres, à ne donner qu’aux gérants.'
              : 'Il est demandé à chaque ouverture, même à un gérant déjà connecté.'}
          </div>
        </div>
      </div>
  )

  if (compact) {
    return (
      <>
        {titre && <h3 className="bloc-fiche-titre">{titre}</h3>}
        {saisie}
      </>
    )
  }

  return (
    <Ecran
      titre={titre ?? 'Bilan'}
      sousTitre={premierCode ? 'Première ouverture' : 'Réservé aux gérants'}
    >
      {saisie}
    </Ecran>
  )
}
